import './css/MapPage.css';

import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

import MapPopup from "../components/MapPopup";
import FilterPanel from "../components/FilterPanel";
import '../components/css/MapPopup.css';

const apiBaseUrl = (process.env.REACT_APP_SERVER_API_URL || "").replace(/\/$/, ""); // remove trailing slash if present
const cartoApiKey = process.env.REACT_APP_CARTO_API_KEY || "";

// Fix for default marker icons when using webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Small component rendered inside MapContainer that has access to the Leaflet map via useMap()
// It will flyTo/setView when `target` changes.
function MapTargetHandler({ target }) {
    const map = useMap();

    useEffect(() => {
        if (!map || !target) return;
        try {
            const zoom = typeof map.getZoom === "function" ? map.getZoom() : undefined;
            if (typeof map.flyTo === "function") {
                map.flyTo(target, zoom, { animate: true });
            } else if (typeof map.setView === "function") {
                map.setView(target, zoom);
            }
        } catch (e) {
            console.warn("MapTargetHandler failed to move map:", e);
        }
    }, [map, target]);

    return null;
}

export default function MapPage() {
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // search query for filtering by name
    const [query, setQuery] = useState("");

    // selected categories (array of enum values)
    const [selectedCategories, setSelectedCategories] = useState([]);

    // target center posted by FilterPanel clicks � MapTargetHandler will act on this when map exists
    const [targetCenter, setTargetCenter] = useState(null);

    const pointsUrl = `${apiBaseUrl}/api/points`;

    useEffect(() => {
        let cancelled = false;

        async function fetchPoints() {
            try {
                const res = await fetch(pointsUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setPoints(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load points:", e);
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : String(e));
                    setPoints([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchPoints();
        return () => { cancelled = true; };
    }, [pointsUrl]);

    // default center if no points
    const defaultCenter = [49.34602, -124.44258];

    // filtered points by search query (case-insensitive, filters by name, address and selected categories)
    const filteredPoints = useMemo(() => {
        const q = String(query ?? "").trim().toLowerCase();
        const selectedSet = new Set(selectedCategories.map(String));
        return points.filter(p => {
            // name and address filter
            if (q) {
                const name = String(p?.name ?? "").toLowerCase();
                const addr = String(p?.civicAddress ?? "").toLowerCase();
                if (!name.includes(q) && !addr.includes(q)) return false;
            }

            // category filter
            if (selectedSet.size > 0) {
                const cats = p?.categoryTypes ?? [];

                // If no categories present on point, it doesn't match when a filter is active
                if (cats.length === 0) return false;

                // Accept the point if any of its categories are selected
                const intersects = cats.some(c => selectedSet.has(String(c)));
                if (!intersects) return false;
            } else {
                // if no categories selected, filter out all points
                return false;
            }

            return true;
        });
    }, [points, query, selectedCategories]);

    const center = filteredPoints.length ? [filteredPoints[0].lat, filteredPoints[0].lng] : defaultCenter;

    // Ensure hooks are always called in same order
    const iconCache = useMemo(() => new Map(), []);

    /**
     * Returns a Leaflet DivIcon with an inline SVG pin filled with `color`.
     * Icons are cached to avoid recreation every render.
     */
    function getIconForColor(rawColor) {
        const color = rawColor ? (String(rawColor).startsWith("#") ? String(rawColor) : `#${String(rawColor)}`) : "#000000";
        if (iconCache.has(color)) return iconCache.get(color);

        const svg = encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
            <path d="M14 0C8 0 3 5 3 11c0 9.2 11 26 11 26s11-16.8 11-26C25 5 20 0 14 0z" fill="${color}" stroke="#000" stroke-opacity="0.12"/>
            <circle cx="14" cy="11" r="4.2" fill="#fff" fill-opacity="0.85"/>
          </svg>
        `);

        const html = `<img src="data:image/svg+xml;charset=utf-8,${svg}" style="display:block; transform: translateY(-2px);" />`;
        const icon = L.divIcon({
            html,
            className: "mappoint-colored-icon", // keep minimal/no default styles
            iconSize: [28, 40],
            iconAnchor: [14, 40] // bottom-center anchor so pin points at coordinate
        });

        iconCache.set(color, icon);
        return icon;
    }

    // helper to robustly extract numeric lat/lng from point object and set targetCenter
    function centerOnPoint(p) {
        if (!p) return;
        const maybeLat = p.lat ?? p.Lat ?? p.Latitude ?? p.latitude;
        const maybeLng = p.lng ?? p.Lng ?? p.Longitude ?? p.longitude;
        const lat = Number(maybeLat);
        const lng = Number(maybeLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            console.warn("Point doesn't contain valid coordinates:", p);
            return;
        }

        // set target; MapTargetHandler will perform the actual map movement when the map exists
        setTargetCenter([lat, lng]);
    }

    // show loading placeholder until points are loaded
    if (loading) {
        return (
            <main className="map-container">
                <div className="map-frame-wrapper d-flex justify-content-center align-items-center">
                    <div className="text-center">
                        <div className="spinner-border" role="status" aria-hidden="true"></div>
                        <div className="mt-2">Loading points...</div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="map-container">
            {/* FilterBar sits above the map (normal flow) */}
            <FilterPanel
                baseUrl={apiBaseUrl}
                visiblePoints={filteredPoints}
                query={query}
                setQuery={setQuery}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                onSelectPoint={centerOnPoint}
            />

            <div className="map-frame-wrapper" style={{ position: "relative" }}>
                {/*<MapLegend />*/}

                {error && (
                    <div className="alert alert-warning m-3" role="alert">
                        Failed to load points: {error}
                    </div>
                )}
                <MapContainer
                    id="worldMap"
                    center={center}
                    zoom={12}
                    style={{ height: "100%", width: "100%" }}
                    doubleClickZoom={false}
                >
                    {/*Original map we used, kept here for reference*/}
                    <TileLayer
                        attribution='&copy; <a href="https://carto.com/attributions">Carto</a> &copy; OpenStreetMap contributors'
                        url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${cartoApiKey}`}
                    />

                    {/*Darker map style, kept here for reference*/}
                    {/*<TileLayer*/}
                    {/*    attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'*/}
                    {/*    url="https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png"*/}
                    {/*/>*/}

                    {/*Black and white map style, kept here for reference, looks good, but might cost money?*/}
                    {/*<TileLayer*/}
                    {/*    attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'*/}
                    {/*    url="https://tiles.stadiamaps.com/tiles/stamen_toner_lite/{z}/{x}/{y}{r}.png"*/}
                    {/*/>*/}

                    {/* Handler that will run inside the MapContainer context and move the map when targetCenter changes */}
                    <MapTargetHandler target={targetCenter} />

                    {filteredPoints.map((p) => {
                        const color = p.colour ?? p.Colour ?? "#000000";
                        return (
                            <Marker
                                key={p.id ?? `${p.lat}-${p.lng}`}
                                position={[p.lat, p.lng]}
                                icon={getIconForColor(color)}
                            >
                                <MapPopup point={p} />
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>
        </main>
    );
}